import { Paper, styled } from '@mui/material'
import { PreviewCardTitle, PreviewCardSubtitle } from './PreviewCardTitle'
import type { ReactNode } from 'react'

export type PreviewCardProps = {
  header?: ReactNode
  content?: ReactNode
  footer?: ReactNode
  'data-testid'?: string
}

const PreviewCardRoot = styled(Paper)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  minWidth: 320,
  maxWidth: 450,
  overflow: 'hidden',
  textAlign: 'left',
  borderRadius: theme.shape['radius-xl'],
  boxShadow: theme.customShadows.sm,
}))

// Section presence is decided by the DOM, not by inspecting the ReactNode: a slot whose
// component renders nothing yields an empty div that `:empty` hides, while falsy-but-renderable
// content like `0` produces a text node and stays visible. Two gotchas the selectors encode:
// `:empty` is defeated by whitespace-only text (don't render a bare `{' '}` into a slot), and
// `display: none` siblings still match `~`, so the separator rule needs `:not(:empty)` on both
// sides or a hidden first section would leave a border at the top of the card.
const PreviewCardSection = styled('div')(({ theme }) => ({
  padding: theme.spacing(4),
  '&:empty': {
    display: 'none',
  },
  '&:not(:empty) ~ &:not(:empty)': {
    borderTop: `${theme.strokeWidth.default} solid ${theme.palette.border.strong}`,
  },
}))

export const PreviewCard = ({ header, content, footer, 'data-testid': dataTestId }: PreviewCardProps) => (
  <PreviewCardRoot data-testid={dataTestId}>
    <PreviewCardSection>{header}</PreviewCardSection>
    <PreviewCardSection>{content}</PreviewCardSection>
    <PreviewCardSection>{footer}</PreviewCardSection>
  </PreviewCardRoot>
)

PreviewCard.Title = PreviewCardTitle
PreviewCard.Subtitle = PreviewCardSubtitle
